import { useContext, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router';

import PropTypes from 'prop-types';

import { useTransl } from 'shared/lib/i18n';

import CommunityMessageSellerFormContainer from '../Messages/CommunityMessageSellerFormContainer';
import CommunityPostFormContainer from './CommunityPostFormContainer';

import CommunityPostItemComponent from './CommunityPostItemComponent';

import { onModalClose, showRenderPropsModal } from '../../Modal/actions';
import {
  confirmDeletePost,
  threadExistsCheck,
  togglePostLike,
} from '../actions';

import { checkHasPermissions, getIsOverflown, isAdmin } from '../../../utils';
import { getFullName } from '../../Person/utils';

import permissions from '../../../permissions';
import { secondaryColor } from '../../../theme';
import CommunityContext from '../context';

const CommunityPostItemContainer = (props) => {
  const { t } = useTransl();
  const history = useHistory();
  const checkIfCommunityMember = useContext(CommunityContext);

  const {
    post,
    onThreadExistsCheck,
    openModal,
    closeModal,
    onRemovePost,
    togglePostLike,
  } = props;
  const {
    id: postId,
    sender,
    subject,
    own: isOwnPost,
    approvalStatus,
    requiresApproval,
  } = post;

  const [isShowComments, setIsShowComments] = useState(false);
  const [isFullTextShown, setIsFullTextShown] = useState(false);
  const [isOverflown, setIsOverflown] = useState(false);

  const textElementRef = useRef(null);

  useEffect(() => {
    textElementRef.current &&
      setIsOverflown(getIsOverflown(textElementRef.current));
  }, [textElementRef.current]);

  if (!postId) return null;

  const onMessageSeller = async () => {
    if (!checkIfCommunityMember()) return;

    const res = await onThreadExistsCheck(postId);
    const { threadExists, thread } = res || {};

    if (threadExists && thread) {
      history.push(`/community/messages/${thread}`);
      return;
    }

    openModal({
      title: t('community.messageTo', { name: getFullName(sender) }),
      maxWidth: 'md',
      closeModal,
      render: () => (
        <CommunityMessageSellerFormContainer
          subject={subject}
          postId={postId}
        />
      ),
    });
  };

  const onEditPost = (post) => {
    if (!checkIfCommunityMember()) return;

    const { type, community } = post;
    const title = `${t('actions.edit')} ${type}`;

    openModal({
      title,
      maxWidth: 'md',
      closeModal,
      render: () => (
        <CommunityPostFormContainer
          currentCommunityId={community?.id}
          type={type}
          post={post}
        />
      ),
    });
  };

  const canDeletePost =
    checkHasPermissions(permissions.delete_community_post) || isOwnPost;
  const canCommentPost =
    !isAdmin() && checkHasPermissions(permissions.add_community_post_comment);
  const canMessageSeller = !isAdmin() && !isOwnPost;
  const isInReview = approvalStatus === 'pending' && requiresApproval;

  const dropDownActions = [
    {
      label: t('title.editPost'),
      onClick: () => onEditPost(post),
      show: isOwnPost,
    },
    {
      label: t('title.deletePost'),
      onClick: () => onRemovePost(postId),
      color: secondaryColor,
      show: canDeletePost,
    },
  ].filter(({ show }) => show);

  const onTogglePostLike = () => {
    if (!checkIfCommunityMember()) return;
    togglePostLike(postId);
  };

  const onToggleComments = () => {
    if (!checkIfCommunityMember()) return;
    setIsShowComments(!isShowComments);
  };

  return (
    <CommunityPostItemComponent
      post={post}
      isShowComments={isShowComments}
      onMessageSeller={onMessageSeller}
      onRemovePost={onRemovePost}
      onToggleComments={onToggleComments}
      onTogglePostLike={onTogglePostLike}
      dropDownActions={dropDownActions}
      isFullTextShown={isFullTextShown}
      setIsFullTextShown={setIsFullTextShown}
      isOverflown={isOverflown}
      textElementRef={textElementRef}
      canCommentPost={canCommentPost}
      canMessageSeller={canMessageSeller}
      isInReview={isInReview}
    />
  );
};

CommunityPostItemContainer.propTypes = {
  post: PropTypes.instanceOf(Object).isRequired,
  onRemovePost: PropTypes.func.isRequired,
  togglePostLike: PropTypes.func.isRequired,
  onThreadExistsCheck: PropTypes.func.isRequired,
  openModal: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
};

const mapDispatchToProps = (dispatch) => ({
  onRemovePost: (postId) => confirmDeletePost(dispatch, postId),
  togglePostLike: (postId) => togglePostLike(dispatch, postId),
  openModal: (props) => showRenderPropsModal(dispatch, props),
  closeModal: () => onModalClose(dispatch),
  onThreadExistsCheck: (postId) => threadExistsCheck(dispatch, postId),
});

export default connect(null, mapDispatchToProps)(CommunityPostItemContainer);
